import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Popconfirm,
  Select,
  Typography,
  Upload,
  message as antMessage,
} from "antd";
import {
  ArrowRightOutlined,
  CameraOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ChatApi, UserApi } from "../../services";
import styles from "./styles.module.css";
import UserAvatar from "../UserAvatar";

const fullName = (u) =>
  u?.full_name ||
  `${u?.first_name || ""} ${u?.last_name || ""}`.trim() ||
  "کاربر";

const initials = (u) => {
  const n = fullName(u);
  return n && n !== "کاربر" ? n.charAt(0) : "؟";
};

const isGroupRoom = (room) => !!room?.title;

export default function ChatInfoPanel({
  open,
  onClose,
  room,
  currentUserId,
  onRoomUpdated,
  compact = false,
}) {
  const queryClient = useQueryClient();

  const group = isGroupRoom(room);
  const isCreator =
    !!room &&
    (room.creator_id === currentUserId || room.creator?.id === currentUserId);
  const canEdit = group && isCreator;

  const other = useMemo(
    () => room?.participants?.find((p) => p.id !== currentUserId),
    [room, currentUserId],
  );

  const [title, setTitle] = useState("");
  const [members, setMembers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setTitle(room?.title || "");
    setMembers(room?.participants ? [...room.participants] : []);
    setUserSearch("");
  }, [room?.id]);

  const { data: users = [] } = useQuery({
    queryKey: ["[Dropdown] Users ChatInfo", userSearch],
    queryFn: () =>
      UserApi.getUsers({
        params: {
          mode: "active",
          per_page: 50,
          search: userSearch || undefined,
        },
      }).then((r) => r.data.users),
    enabled: canEdit && open,
    initialData: [],
  });

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const dirty = useMemo(() => {
    if (!room) return false;
    const origIds = (room.participants || []).map((p) => p.id).sort();
    const curIds = members.map((m) => m.id).sort();
    const idsChanged =
      origIds.length !== curIds.length ||
      origIds.some((id, i) => id !== curIds[i]);
    return idsChanged || (title || "") !== (room.title || "");
  }, [room, members, title]);

  const updateMutation = useMutation(ChatApi.updateChatRoom, {
    onSuccess: (res) => {
      const updated =
        res?.data?.chat_room || res?.data?.data || {
          ...room,
          title,
          participants: members,
        };
      queryClient.invalidateQueries(["[List] ChatRooms"]);
      onRoomUpdated?.({ ...room, ...updated });
      antMessage.success("اطلاعات گفتگو بروزرسانی شد");
    },
    onError: () => antMessage.error("خطا در بروزرسانی گفتگو"),
  });

  const deleteAvatarMutation = useMutation(ChatApi.deleteChatMedia, {
    onSuccess: () => {
      queryClient.invalidateQueries(["[List] ChatRooms"]);
      onRoomUpdated?.({ ...room, avatar: null });
      antMessage.success("عکس گروه حذف شد");
    },
    onError: () => antMessage.error("خطا در حذف عکس"),
  });

  const handleSave = () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      antMessage.warning("نام گروه نمی‌تواند خالی باشد");
      return;
    }

    const ids = [...new Set([...members.map((m) => m.id), currentUserId])];
    updateMutation.mutate({
      params: { id: room.id },
      body: { title: cleanTitle, participants: ids },
    });
  };

  const addMember = (userId) => {
    if (memberIds.has(userId)) return;
    const u = users.find((x) => x.id === userId);
    if (u) setMembers((prev) => [...prev, u]);
  };

  const removeMember = (userId) => {
    if (userId === currentUserId) return;
    setMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  if (!room) return null;

  const roomAvatar = room.avatar?.original_url;
  const otherAvatar = other?.avatar?.original_url;

  return (
    <div
      className={`${styles.panel} ${open ? styles.panelOpen : ""} ${
        compact ? styles.compact : ""
      }`}
    >
      {}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onClose} aria-label="بستن">
          {compact ? <ArrowRightOutlined /> : <CloseOutlined />}
        </button>
        <span className={styles.headerTitle}>
          {group ? "اطلاعات گروه" : "اطلاعات مخاطب"}
        </span>
      </div>

      <div className={styles.body}>
        {}
        <div className={styles.identity}>
          <div className={styles.avatarWrap}>
            {group ? (
              canEdit ? (
                <Upload
                  showUploadList={false}
                  accept="image/png, image/gif, image/jpeg"
                  customRequest={(event) => {
                    const ids = [
                      ...new Set([...members.map((m) => m.id), currentUserId]),
                    ];
                    updateMutation.mutate({
                      params: { id: room.id },
                      body: {
                        title: (title || room.title || "").trim(),
                        participants: ids,
                        avatar: event.file,
                      },
                    });
                  }}
                >
                  <div className={styles.avatarEditable}>
                    <Avatar
                      size={compact ? 72 : 96}
                      src={roomAvatar}
                      icon={<TeamOutlined />}
                      className={styles.groupAvatar}
                    />
                    <span className={styles.avatarOverlay}>
                      {updateMutation.isLoading ? (
                        <LoadingOutlined />
                      ) : (
                        <CameraOutlined />
                      )}
                    </span>
                  </div>
                </Upload>
              ) : (
                <Avatar
                  size={compact ? 72 : 96}
                  src={roomAvatar}
                  icon={<TeamOutlined />}
                  className={styles.groupAvatar}
                />
              )
            ) : (
              <Avatar
                size={compact ? 72 : 96}
                src={otherAvatar}
                icon={<UserOutlined />}
              >
                {!otherAvatar && initials(other)}
              </Avatar>
            )}
          </div>

          {group && canEdit ? (
            <Typography.Paragraph
              className={styles.identityName}
              style={{ margin: 0 }}
              editable={{
                onChange: (val) => setTitle(val),
                text: title,
                tooltip: "ویرایش نام گروه",
                maxLength: 60,
              }}
            >
              {title || "نام گروه"}
            </Typography.Paragraph>
          ) : (
            <span className={styles.identityName}>
              {group ? room.title : fullName(other)}
            </span>
          )}

          <span className={styles.identitySub}>
            {group
              ? `${room.participants?.length ?? 0} عضو`
              : "گفتگوی خصوصی"}
          </span>

          {group && canEdit && room.avatar && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteAvatarMutation.isLoading}
              onClick={() =>
                deleteAvatarMutation.mutate({
                  params: { chat: room.id, media: room.avatar.uuid },
                })
              }
              className={styles.removeAvatarBtn}
            >
              حذف عکس
            </Button>
          )}
        </div>

        {}
        {group && (
          <div className={styles.membersSection}>
            <div className={styles.sectionTitle}>
              <TeamOutlined /> اعضا ({members.length})
            </div>

            {canEdit && (
              <Select
                mode="multiple"
                showSearch
                allowClear={false}
                value={[]}
                placeholder="افزودن عضو..."
                filterOption={false}
                onSearch={setUserSearch}
                onSelect={addMember}
                className={styles.addSelect}
                suffixIcon={<UserAddOutlined />}
                options={users
                  .filter((u) => !memberIds.has(u.id))
                  .map((u) => ({
                    label: fullName(u),
                    value: u.id,
                    avatar: u.avatar?.original_url,
                  }))}
                optionRender={(opt) => (
                  <div className={styles.optRow}>
                    <Avatar
                      size={22}
                      src={opt.data.avatar}
                      icon={<UserOutlined />}
                    />
                    <span>{opt.data.label}</span>
                  </div>
                )}
              />
            )}

            <div className={styles.memberList}>
              {members.map((m) => (
                <div key={m.id} className={styles.memberRow}>
                  <UserAvatar user={m} size={38} />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>{fullName(m)}</span>
                    {(m.id === room.creator_id ||
                      m.id === room.creator?.id) && (
                      <span className={styles.creatorTag}>سازنده</span>
                    )}
                    {m.id === currentUserId && (
                      <span className={styles.youTag}>شما</span>
                    )}
                  </div>
                  {canEdit && m.id !== currentUserId && (
                    <Popconfirm
                      title="حذف عضو"
                      description={`${fullName(m)} از گروه حذف شود؟`}
                      okText="بله"
                      cancelText="خیر"
                      onConfirm={() => removeMember(m.id)}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<CloseOutlined />}
                      />
                    </Popconfirm>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {}
      {canEdit && (
        <div className={styles.footer}>
          <Button
            type="primary"
            block
            icon={<SaveOutlined />}
            disabled={!dirty}
            loading={updateMutation.isLoading}
            onClick={handleSave}
          >
            ذخیره تغییرات
          </Button>
        </div>
      )}
    </div>
  );
}
